import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import { orderIsFreeDutiesEligible } from "../../utils/orderIsFreeDutiesEligible";

import { RequestDutiesRefundCTA } from "./ctas";

export const RequestDutiesReimbursementIA: InstantAnswerBuilderFn = (
  strictLocalize,
  articles
) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize, articles)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize, articles)?.getLink?.(
          context.brandOrder
        );
      },
    },
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder } = context;
  if (!brandOrder) {
    return ORDER_NOT_SELECTED(strictLocalize);
  }

  return orderIsFreeDutiesEligible(brandOrder)
    ? DUTIES_FREE_ORDER(strictLocalize)
    : NON_DUTIES_FREE_ORDER(strictLocalize);
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize, articles) => {
  const { brandOrder } = context;
  if (!brandOrder) {
    return RequestDutiesRefundCTA(strictLocalize, articles);
  }

  if (!orderIsFreeDutiesEligible(brandOrder)) {
    return undefined;
  }

  return RequestDutiesRefundCTA(strictLocalize, articles);
};

const ORDER_NOT_SELECTED = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => DUTIES_REIMBURSEMENT(strictLocalize),
  getDescription: () => [
    WE_WILL_APPLY_DISCOUNT_IF_DUTIES_ARE_COVERED(strictLocalize),
    YOU_WILL_PAY_THE_CARRIER_IF_DUTIES_ARE_NOT_COVERED(strictLocalize),
    EXCLUDE_TAXES_AND_FEES_FROM_REIMBURSEMENT_REQUEST(strictLocalize),
  ],
});

const DUTIES_FREE_ORDER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => DUTIES_REIMBURSEMENT(strictLocalize),
  getDescription: () => [
    ORDER_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION(strictLocalize),
    EXCLUDE_TAXES_AND_FEES_FROM_REIMBURSEMENT_REQUEST(strictLocalize),
  ],
});

const NON_DUTIES_FREE_ORDER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => DUTIES_REIMBURSEMENT(strictLocalize),
  getDescription: () => [
    ORDER_NOT_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION(strictLocalize),
  ],
});

const DUTIES_REIMBURSEMENT = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Duties reimbursement",
    description: {
      text: "Title for the answer of change shipping address for an order issue if the selected order has shipped",
    },
  });

const WE_WILL_APPLY_DISCOUNT_IF_DUTIES_ARE_COVERED = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "If duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order. The carrier will send you an invoice for all import fees (including import duties and VAT), which you’ll pay in full. If the actual cost of import duties and disbursement fees is greater than the promotion you received, you can request a refund in Faire credit for the difference when the order is delivered.",
    description: {
      text: "Part of the support answer shown to retailers explaining how duties work with Faire orders",
    },
  });

const YOU_WILL_PAY_THE_CARRIER_IF_DUTIES_ARE_NOT_COVERED = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "If duties are not covered for your order, you will pay the carrier in full directly and will not be able to request reimbursement for the duties.",
    description: {
      text: "Part of the support answer shown to retailers explaining how duties work with Faire orders",
    },
  });

const EXCLUDE_TAXES_AND_FEES_FROM_REIMBURSEMENT_REQUEST = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "Please note that VAT, excise taxes, and other fees are not covered by Faire so exclude those costs while filing for reimbursement.",
    description: {
      text: "Part of the support answer shown to retailers explaining how duties work with Faire orders",
    },
  });

const ORDER_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "This order is eligible for free duties promotion. If duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order. The carrier will send you an invoice for all import fees (including import duties and VAT), which you’ll pay in full. If the actual cost of import duties and disbursement fees is greater than the promotion you received, you can request a refund in Faire credit for the difference when the order is delivered.",
    description: {
      text: "Part of the support answer shown to retailers in relation to an order that is eligible for free duties",
    },
  });

const ORDER_NOT_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "This order is not eligible for free duties promotion and therefore you will not be able to request duties cost reimbursement. The carrier will send you an invoice for all import fees (including import duties and VAT), which you’ll pay in full.",
    description: {
      text: "Part of the support answer shown to retailers in relation to an order that is not eligible for free duties",
    },
  });
